import { findRegimen } from "@/data/meal-plan";
import type { ChoiceGroupSelections, DayOfWeek, DayPlan, MealSelections, WeekPlan } from "@/types/meal-plan-state";
import { isDayUntouched } from "./plan-switch";
import { createEmptyDaySelections } from "./random-plan";

const WEEK_DAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// ── Semantic equality ──
//
// Weeks are compared by meaning, not by JSON text: reducers, `copyMealsFromDay`
// and `setDayMealMenu` build the same content with different key orders, and
// a day without notes may hold `mealNotes: {}` or no key at all. A string
// compare would fire saves for nothing and report false conflicts.

/** Deep equality that ignores key order and treats `undefined` values as missing keys (JSON semantics). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, i) => deepEqual(value, b[i]));
  }
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  const keysA = Object.keys(ra).filter((k) => ra[k] !== undefined);
  const keysB = Object.keys(rb).filter((k) => rb[k] !== undefined);
  return keysA.length === keysB.length && keysA.every((k) => deepEqual(ra[k], rb[k]));
}

/** Per-slot eaten flags with trailing `false`s dropped: `[false]`, `[]` and missing all mean "nothing eaten". */
function normalizedEaten(cg: ChoiceGroupSelections | undefined): boolean[] {
  const eaten = (cg?.eaten ?? []).map(Boolean);
  while (eaten.length > 0 && !eaten[eaten.length - 1]) eaten.pop();
  return eaten;
}

function eatenEquals(a: ChoiceGroupSelections | undefined, b: ChoiceGroupSelections | undefined): boolean {
  return deepEqual(normalizedEaten(a), normalizedEaten(b));
}

/** Same picks and skip state; `eaten` is not compared. */
function choiceContentEquals(a: ChoiceGroupSelections | undefined, b: ChoiceGroupSelections | undefined): boolean {
  if (!a || !b) return a === b;
  return deepEqual(a.selections, b.selections) && Boolean(a.skipped) === Boolean(b.skipped);
}

function compareDays(a: DayPlan, b: DayPlan, ignoreEaten: boolean): boolean {
  if ((a.regimenId ?? null) !== (b.regimenId ?? null)) return false;
  if (!deepEqual(a.mealNotes ?? {}, b.mealNotes ?? {})) return false;
  if (!deepEqual(a.mealMenus ?? {}, b.mealMenus ?? {})) return false;
  if (!a.meals || !b.meals) return !a.meals && !b.meals;
  if (a.meals.length !== b.meals.length) return false;
  return a.meals.every((mealA, mi) => {
    const mealB = b.meals![mi];
    if (!mealA || !mealB) return mealA === mealB;
    if (mealA.length !== mealB.length) return false;
    return mealA.every(
      (cgA, ci) => choiceContentEquals(cgA, mealB[ci]) && (ignoreEaten || eatenEquals(cgA, mealB[ci])),
    );
  });
}

/**
 * Semantic day equality: key order is ignored, a missing `mealNotes` /
 * `mealMenus` equals `{}`, and an `eaten` without any `true` equals none.
 */
export function dayEquals(a: DayPlan, b: DayPlan): boolean {
  return compareDays(a, b, false);
}

export function weeksEqual(a: WeekPlan, b: WeekPlan): boolean {
  return a === b || WEEK_DAYS.every((day) => dayEquals(a[day], b[day]));
}

// ── Rebase ──
//
// Per meal: a meal is its selections (with "comido" ticks), its note
// (`mealNotes[i]`) and its chosen menu (`mealMenus[i]`). Two edits of the
// same day merge as long as they touch different meals. A change of the
// day's regimen (or of whether it has meals) changes what every meal index
// means, so it is merged, or reported, for the whole day.

/**
 * True when the choice group at (mi, ci) means the same thing (same regimen
 * and menu) and holds the same picks and skip state in both days; `eaten`
 * is not compared.
 */
export function choiceUnchanged(day: DayPlan, base: DayPlan, mi: number, ci: number): boolean {
  if ((day.regimenId ?? null) !== (base.regimenId ?? null)) return false;
  if (day.mealMenus?.[mi] !== base.mealMenus?.[mi]) return false;
  return choiceContentEquals(day.meals?.[mi]?.[ci], base.meals?.[mi]?.[ci]);
}

/**
 * Meal `mi` of `target` with `source`'s eaten flags copied into every choice
 * group where neither side re-picked since `base`, `source` ticked something
 * and `target` didn't. A group re-picked on either side keeps `target`'s
 * flags. Returns `target`'s own array when nothing changed.
 */
function mergeMealEaten(target: DayPlan, base: DayPlan, source: DayPlan, mi: number): MealSelections | undefined {
  const meal = target.meals?.[mi];
  if (!meal) return meal;
  let changed = false;
  const merged = meal.map((cg, ci) => {
    if (!choiceUnchanged(target, base, mi, ci) || !choiceUnchanged(source, base, mi, ci)) return cg;
    const baseCg = base.meals?.[mi]?.[ci];
    const sourceCg = source.meals?.[mi]?.[ci];
    if (eatenEquals(sourceCg, baseCg) || !eatenEquals(cg, baseCg)) return cg;
    changed = true;
    const eaten = sourceCg?.eaten;
    const { eaten: _dropped, ...rest } = cg;
    return eaten ? { ...rest, eaten: [...eaten] } : rest;
  });
  return changed ? merged : meal;
}

/** `target` with `source`'s still-applicable eaten flags (see `mergeMealEaten`) in every meal. */
function mergeEaten(target: DayPlan, base: DayPlan, source: DayPlan): DayPlan {
  if (!target.meals) return target;
  const meals = target.meals.map((meal, mi) => mergeMealEaten(target, base, source, mi) ?? meal);
  return meals.some((meal, mi) => meal !== target.meals![mi]) ? { ...target, meals } : target;
}

/** Same note, menu and choice groups (eaten flags compared unless `ignoreEaten`) for meal `mi`. */
function mealEquals(a: DayPlan, b: DayPlan, mi: number, ignoreEaten: boolean): boolean {
  if (a.mealNotes?.[mi] !== b.mealNotes?.[mi]) return false;
  if (a.mealMenus?.[mi] !== b.mealMenus?.[mi]) return false;
  const mealA = a.meals?.[mi];
  const mealB = b.meals?.[mi];
  if (!mealA || !mealB) return mealA === mealB;
  if (mealA.length !== mealB.length) return false;
  return mealA.every(
    (cgA, ci) => choiceContentEquals(cgA, mealB[ci]) && (ignoreEaten || eatenEquals(cgA, mealB[ci])),
  );
}

/** Same regimen and meal count: meal `i` means the same meal in every day. */
function sameStructure(a: DayPlan, b: DayPlan): boolean {
  if ((a.regimenId ?? null) !== (b.regimenId ?? null)) return false;
  if (!a.meals || !b.meals) return !a.meals && !b.meals;
  return a.meals.length === b.meals.length;
}

/** Every meal index a day talks about: its meals plus any note or menu key. */
function mealIndices(...days: DayPlan[]): number[] {
  const indices = new Set<number>();
  for (const day of days) {
    day.meals?.forEach((_, mi) => indices.add(mi));
    for (const key of Object.keys(day.mealNotes ?? {})) indices.add(Number(key));
    for (const key of Object.keys(day.mealMenus ?? {})) indices.add(Number(key));
  }
  return [...indices].sort((x, y) => x - y);
}

/** Set (or, for `undefined`, remove) key `mi` of a per-meal record. */
function withMealKey<T>(record: Record<number, T> | undefined, mi: number, value: T | undefined): Record<number, T> {
  const next = { ...record };
  if (value === undefined) delete next[mi];
  else next[mi] = value;
  return next;
}

/**
 * Per-meal three-way merge of a day whose structure (regimen, meal count)
 * all three sides share. Meals only I changed are mine, meals only they
 * changed are theirs, "comido" ticks merge per choice group, and a meal both
 * changed takes theirs and makes the day a conflict. Returns `theirs` itself
 * when nothing of mine survives.
 */
function rebaseMeals(
  base: DayPlan,
  mine: DayPlan,
  theirs: DayPlan,
): { day: DayPlan; conflict: boolean; meals: number[] } {
  let day = theirs;
  let conflict = false;
  const conflictMeals: number[] = [];
  for (const mi of mealIndices(base, mine, theirs)) {
    // Only they changed it, nobody did, or both made the same change: theirs.
    if (mealEquals(mine, base, mi, false) || mealEquals(mine, theirs, mi, false)) continue;
    let meal: MealSelections | undefined;
    let from: DayPlan;
    if (mealEquals(theirs, base, mi, false)) {
      // Only I changed it.
      meal = mine.meals?.[mi];
      from = mine;
    } else if (mealEquals(theirs, base, mi, true)) {
      // They only ticked "comido": keep my meal, add their ticks where they still apply.
      meal = mergeMealEaten(mine, base, theirs, mi);
      from = mine;
    } else if (mealEquals(mine, base, mi, true)) {
      // I only ticked "comido": keep their meal, re-apply my ticks where they still apply.
      meal = mergeMealEaten(theirs, base, mine, mi);
      if (meal === theirs.meals?.[mi]) continue;
      from = theirs;
    } else {
      // Both changed this meal: first save wins.
      conflict = true;
      conflictMeals.push(mi);
      continue;
    }
    day = {
      ...day,
      meals: day.meals && meal ? day.meals.map((m, i) => (i === mi ? meal : m)) : day.meals,
      mealNotes: withMealKey(day.mealNotes, mi, from.mealNotes?.[mi]),
      mealMenus: withMealKey(day.mealMenus, mi, from.mealMenus?.[mi]),
    };
  }
  if (day === theirs) return { day, conflict, meals: conflictMeals };
  // Keep the stored shape tidy: no empty record theirs didn't have.
  const { mealNotes, mealMenus, ...rest } = day;
  const keep = (record: Record<number, unknown> | undefined, had: unknown) =>
    record !== undefined && (had !== undefined || Object.keys(record).length > 0);
  return {
    day: {
      ...rest,
      ...(keep(mealNotes, theirs.mealNotes) ? { mealNotes } : {}),
      ...(keep(mealMenus, theirs.mealMenus) ? { mealMenus } : {}),
    },
    conflict,
    meals: conflictMeals,
  };
}

/**
 * The base to merge against. When the day was untouched at the base and both
 * sides now plan it in the same regimen (two devices created the week, or
 * adopted the plan, from the same presets), a fresh day of that regimen is
 * what both started from: their creation isn't a change of mine to fight.
 */
function effectiveBase(base: DayPlan, mine: DayPlan, theirs: DayPlan): DayPlan {
  const regimenId = mine.regimenId ?? null;
  if (!isDayUntouched(base) || regimenId === null || regimenId !== (theirs.regimenId ?? null)) return base;
  if (isDayUntouched(theirs)) return theirs;
  if (sameStructure(base, mine)) return base;
  if (isDayUntouched(mine)) return mine;
  const regimen = findRegimen(regimenId);
  return regimen ? { regimenId, meals: createEmptyDaySelections(regimen) } : base;
}

/** `meals`: the meal indices both sides changed, when the conflict is per meal (absent: the whole day). */
function rebaseDay(
  base: DayPlan,
  mine: DayPlan,
  theirs: DayPlan,
): { day: DayPlan; conflict: boolean; meals?: number[] } {
  base = effectiveBase(base, mine, theirs);
  // Only they changed it (or nobody did).
  if (dayEquals(mine, base)) return { day: theirs, conflict: false };
  // Only I changed it.
  if (dayEquals(theirs, base)) return { day: mine, conflict: false };
  // Both made the same change.
  if (dayEquals(mine, theirs)) return { day: theirs, conflict: false };
  // Both edited the day's meals within the same regimen: merge meal by meal.
  if (sameStructure(base, mine) && sameStructure(base, theirs)) return rebaseMeals(base, mine, theirs);
  // A regimen change on one side: the whole day is the unit.
  // They only ticked "comido": keep my day, add their ticks where they still apply.
  if (compareDays(theirs, base, true)) return { day: mergeEaten(mine, base, theirs), conflict: false };
  // I only ticked "comido": take their day, re-apply my ticks where they still apply.
  if (compareDays(mine, base, true)) return { day: mergeEaten(theirs, base, mine), conflict: false };
  // Real conflict: first save wins.
  return { day: theirs, conflict: true };
}

/**
 * Three-way merge of my unsaved week onto the server's newer copy, per day
 * and, within a day, per meal. `base` is the server copy my edits started
 * from. A meal (or, after a regimen change, a day) both sides changed takes
 * theirs (first save wins); its day is listed in `conflictDays` and, when
 * only some of its meals clashed, their indices in `conflictMeals[day]`.
 */
export function rebaseWeek(
  base: WeekPlan,
  mine: WeekPlan,
  theirs: WeekPlan,
): { plan: WeekPlan; conflictDays: DayOfWeek[]; conflictMeals: Partial<Record<DayOfWeek, number[]>> } {
  const plan = { ...theirs };
  const conflictDays: DayOfWeek[] = [];
  const conflictMeals: Partial<Record<DayOfWeek, number[]>> = {};
  for (const day of WEEK_DAYS) {
    const merged = rebaseDay(base[day], mine[day], theirs[day]);
    plan[day] = merged.day;
    if (merged.conflict) conflictDays.push(day);
    if (merged.conflict && merged.meals?.length) conflictMeals[day] = merged.meals;
  }
  return { plan, conflictDays, conflictMeals };
}
